import { db } from "./db.js";
import { ref } from "vue";
import { isAdminOrOwner } from "./permissions.js";
import { getMemberCaching, getCachedMemberRole } from "./teams.js";

// ==================== CREATE ====================
function createLog(firmId, data) {
  const ref = db.collection(`firms/${firmId}/logs`).doc();
  return ref.set(data);
}

// ==================== READ ====================

async function getLogs(firmId) {
  const logs = ref([]);
  const unsubscribe = db.collection(`firms/${firmId}/logs`)
    .orderBy("createdAt", "desc")
    .onSnapshot((snapshot) => {
      logs.value = snapshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
    });
  return { logs, unsubscribe };
}

async function getTrademarkLogs(firmId) {
  const logs = ref([]);
  const unsubscribe = db.collection(`firms/${firmId}/additions/tm-addition/trademark-logs`)
    .orderBy("createdAt", "desc")
    .onSnapshot((snapshot) => {
      logs.value = snapshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
    });
  return { logs, unsubscribe };
}

// Role-aware variant (mirrors streamTrademarksOfUser): firestore.rules scope
// log reads to admins/managers/owners or staff whose uid is in the log's
// assigned[] (denormalized from the trademark by the assignment flow). Uses
// the cached role to attach instantly, re-checking membership in the
// background.
async function getTrademarkLogsOfUser(firmId, userId) {
  const logs = ref([]);
  const collectionRef = db.collection(`firms/${firmId}/additions/tm-addition/trademark-logs`);
  const queryFor = (role) =>
    role === "admin" || role === "manager"
      ? collectionRef.orderBy("createdAt", "desc")
      : collectionRef.where("assigned", "array-contains", userId).orderBy("createdAt", "desc");
  const onNext = (snapshot) => {
    logs.value = snapshot.docs.map((doc) => {
      return { id: doc.id, ...doc.data() };
    });
  };
  const onError = (error) => { console.log(error); };

  const cachedRole = getCachedMemberRole(firmId, userId);
  if (cachedRole) {
    let unsubscribe = queryFor(cachedRole).onSnapshot(onNext, onError);
    getMemberCaching(firmId, userId).then((member) => {
      const freshRole = (member && member.role) || "member";
      if (freshRole !== cachedRole) {
        unsubscribe();
        unsubscribe = queryFor(freshRole).onSnapshot(onNext, onError);
      }
    });
    return { logs, unsubscribe: () => unsubscribe() };
  }

  const member = await getMemberCaching(firmId, userId);
  const unsubscribe = queryFor((member && member.role) || "member").onSnapshot(onNext, onError);
  return { logs, unsubscribe };
}

async function getLogsOfUser(firmId, userId) {
  const logs = ref([]);
  const is_admin_owner = await isAdminOrOwner(userId, firmId);

  return new Promise((resolve, reject) => {
    const collectionRef = db.collection(`firms/${firmId}/logs`);
    let query;

    if (is_admin_owner) {
      query = collectionRef;
    } else {
      query = collectionRef.where("assigned", "array-contains", userId);
    }

    const unsubscribe = query.onSnapshot((querySnapshot) => {
      logs.value = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      sortLogsByCreatedAt(logs.value);
      resolve({ logs, unsubscribe }); // Resolve with logs and unsubscribe function
    }, reject);
  });
}

// sort logs by deadline
function sortLogsByCreatedAt(logs) {
  logs.sort(function (a, b) {
    var keyA = a.deadline != null ? a.createdAt.toDate() : new Date("1800-01-01"),
      keyB = b.deadline != null ? b.createdAt.toDate() : new Date("1800-01-01");
    // Compare the 2 dates
    if (keyA < keyB) return -1;
    if (keyA > keyB) return 1;
    return 0;
  });
}

async function getLogsByTime(firmId, time) {
  const logs = ref([]);
  const unsubscribe = db.collection(`firms/${firmId}/logs`)
    .orderBy("createdAt", "desc")
    .onSnapshot((snapshot) => {
      logs.value = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((log) => log.createdAt.toMillis() >= time.toMillis() && log.status !== "error");
    });
  return { logs, unsubscribe };
}

async function getLogsByApplicationId(firmId, applId) {
  const logs = ref([]);
  await db.collection(`firms/${firmId}/logs`)
    .where("applId", "==", applId)
    .get()
    .then((querySnaptshot) => {
      logs.value = querySnaptshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
    });
  return logs;
}

async function getLogsByStatus(firmId, status) {
  const logs = ref([]);
  db.collection(`firms/${firmId}/logs`)
    .where("status", "==", status)
    .onSnapshot((snapshot) => {
      logs.value = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    });
  return logs;
}

// ==================== NO UPDATE ALLOWED ====================

async function updateLog(firmId, logId, data, transaction = null) {
  const logRef = db.doc(`firms/${firmId}/logs/${logId}`);
  if (transaction) {
    transaction.update(logRef, data);
  } else {
    await logRef.update(data);
  }
}

async function updateTrademarkLog(firmId, logId, data, transaction = null) {
  const logRef = db.doc(`firms/${firmId}/additions/tm-addition/trademark-logs/${logId}`);
  if (transaction) {
    transaction.update(logRef, data);
  } else {
    await logRef.update(data);
  }
}

async function getTrademarkLogsBySerialNumber(firmId, serialNumber) {
  const logs = ref([]);
  await db.collection(`firms/${firmId}/additions/tm-addition/trademark-logs`)
    .where("serialNumber", "==", serialNumber)
    .get()
    .then((querySnapshot) => {
      logs.value = querySnapshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
    });
  return logs;
}

// ==================== NO DELETE ALLOWED ====================

export {
  createLog,
  getLogs,
  getTrademarkLogs,
  getTrademarkLogsOfUser,
  getTrademarkLogsBySerialNumber,
  getLogsByStatus,
  getLogsByTime,
  getLogsByApplicationId,
  getLogsOfUser,
  updateLog,
  updateTrademarkLog
};
